import React, { useState } from 'react';
import { Box, Button, Container, createTheme, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PublicLocale, publicPath } from './routes';
import { colors, fonts } from '../../../theme/brandTokens';
export { colors, fonts } from '../../../theme/brandTokens';
export const contactPhone = { label: '+46 40 675 05 20', href: 'tel:+46406750520' };
export const homeContactLabel: Record<PublicLocale, string> = { sv: 'Kontakta oss', en: 'Contact us' };
export const publicTheme = createTheme({
  palette: {
    primary: { main: colors.rust, dark: colors.rustDark },
    secondary: { main: colors.graphite },
    background: { default: colors.paper, paper: colors.white },
    text: { primary: colors.ink, secondary: colors.muted },
    divider: colors.line,
  },
  typography: {
    fontFamily: fonts.sans,
    h1: { fontWeight: 700, letterSpacing: '-0.05em', lineHeight: 1.04 },
    h2: { fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.08 },
    h3: { fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
    body1: { lineHeight: 1.65 }, body2: { lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 2 },
  components: {
    MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { padding: '12px 18px', minHeight: 44 } } },
    MuiTab: { styleOverrides: { root: { textTransform: 'none', minHeight: 48 } } },
    MuiLink: { defaultProps: { underline: 'hover' } },
    MuiButtonBase: { styleOverrides: { root: { '&.Mui-focusVisible': { outline: `3px solid ${colors.copper}`, outlineOffset: 3 } } } },
  },
});
export const headingSx = { fontSize: { xs: '2.2rem', md: '3.35rem' }, maxWidth: 850 };
export const splitSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 9 }, alignItems: 'center' };
export const cardSx = { p: { xs: 3, md: 4 }, border: `1px solid ${colors.line}`, bgcolor: colors.white, minWidth: 0 };

export function Frame({ children }: { children: React.ReactNode }) {
  return <Container maxWidth={false} sx={{ maxWidth: 1320, px: { xs: 2.5, sm: 4, md: 6 } }}>{children}</Container>;
}
export function Chapter({ children, id, dark = false, tone, compact = false }: {
  children: React.ReactNode; id?: string; dark?: boolean; tone?: 'soft' | 'rust'; compact?: boolean;
}) {
  return <Box component="section" id={id} sx={{
    py: compact ? { xs: 4, md: 5 } : { xs: 7, md: 11 },
    borderTop: tone === 'rust' ? 0 : `1px solid ${dark ? '#424B42' : colors.line}`,
    bgcolor: tone === 'rust' ? colors.rust : tone === 'soft' ? colors.soft : dark ? colors.graphite : colors.paper,
    color: dark || tone === 'rust' ? colors.white : colors.ink,
    scrollMarginTop: 96,
  }}><Frame>{children}</Frame></Box>;
}
export function Eyebrow({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return <Typography component="p" sx={{ fontFamily: fonts.mono, fontSize: 12, fontWeight: 600, letterSpacing: '.08em', mb: 2.5, color: dark ? colors.copper : colors.muted }}>{children}</Typography>;
}
export function Intro({ label, title, children }: { label: string; title: string; children?: React.ReactNode }) {
  return <Chapter><Eyebrow>{label}</Eyebrow><Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', md: '4.8rem' }, maxWidth: 940, mb: 3, whiteSpace: 'pre-line' }}>{title}</Typography>
    {children && <Typography sx={{ maxWidth: 730, fontSize: { xs: 17, md: 20 }, color: colors.muted }}>{children}</Typography>}
  </Chapter>;
}
export function Action({ href, children, secondary = false, quiet = false, onClick }: {
  href: string; children: React.ReactNode; secondary?: boolean; quiet?: boolean; onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) {
  return <Button href={href} onClick={onClick} variant={quiet ? 'text' : secondary ? 'outlined' : 'contained'} endIcon={<ArrowForwardIcon />}
    sx={quiet ? { color: 'inherit', px: 0, textDecoration: 'underline', textUnderlineOffset: 5 } : secondary ? { color: 'inherit', borderColor: 'currentColor' } : {}}>{children}</Button>;
}
// Approved KlaraView brand-kit horizontal lockup (Grafit/Oxid on light,
// White/Oxid on dark). Replaces the earlier placeholder mark used pre-launch.
export function Wordmark({ dark = false }: { dark?: boolean }) {
  return <Box component="img"
    src={dark ? '/brand/klaraview-logo-horizontal-dark.svg' : '/brand/klaraview-logo-horizontal-light.svg'}
    alt="KlaraView" sx={{ height: 32, width: 'auto', display: 'block' }} />;
}

interface PublicImageAsset {
  src: string;
  srcSet?: string;
  width?: number;
  height?: number;
  alt: string;
  caption: string;
}
const optimizedSrcSet = (name: 'facade' | 'pilot' | 'portal-portfolio' | 'portal-review' | 'portal-hero') => [480, 960, 1440, 1920]
  .map(width => `/images/landing/optimized/${name}-${width}.webp ${width}w`).join(', ');
const photoSizes = '(min-width: 1320px) 600px, (min-width: 900px) calc((100vw - 144px) / 2), (min-width: 600px) calc(100vw - 64px), calc(100vw - 40px)';

type PhotoKey = 'facade' | 'pilot' | 'controller';
export type PortalViewKey = 'portalHero' | 'portalPortfolio' | 'portalReview';
export const images: Record<PhotoKey | PortalViewKey, PublicImageAsset> = {
  facade: { src: '/images/landing/PXL_20251214_073827262.jpg', srcSet: optimizedSrcSet('facade'), width: 3000, height: 4000, alt: 'Tegelfasad och drönare under en KlaraView-flygning', caption: 'KlaraViews miljöfoto. Illustrativa markeringar är inte en skadeanalys av byggnaden.' },
  pilot: { src: '/images/landing/process-step-1-datacapture.jpg', srcSet: optimizedSrcSet('pilot'), width: 3000, height: 4000, alt: 'Drönarpilot vid en fastighet i Malmö', caption: 'Från KlaraViews arbete på plats.' },
  controller: { src: '/images/landing/PXL_20251214_075628158.jpg', alt: 'Pilotens kontroll med byggnadsbild under en flygning', caption: 'Bildinsamling med människan bakom tekniken.' },
  // Portal views of the demo-website sample customer (scripts/demo/seed_website_portfolio.py), without repair costs.
  portalHero: { src: '/images/landing/portal-hero-detail.jpg', srcSet: optimizedSrcSet('portal-hero'), width: 1920, height: 1640, alt: 'Närbild från KlaraView-portalen: markerad spricka i putsen under takfoten', caption: 'Markerad spricka i KlaraViews drönarbild. Beskuret exempel från portalen.' },
  portalPortfolio: { src: '/images/landing/portal-portfolio.jpg', srcSet: optimizedSrcSet('portal-portfolio'), width: 1920, height: 1160, alt: 'KlaraView-portalen med en lista över exempelfastigheter, med en fastighet markerad med hög prioritet', caption: 'Portalvy med exempelfastigheter. En fastighet är illustrativt markerad med hög prioritet utifrån en bedömning, inte utifrån antal fynd eller AI-säkerhet.' },
  portalReview: { src: '/images/landing/portal-defect-review.jpg', srcSet: optimizedSrcSet('portal-review'), width: 1920, height: 877, alt: 'KlaraView-portalen med en markerad spricka i en taklist och ingenjörens kommentar och rekommendation bredvid bilden', caption: 'Exempel från portalen. Bilden, platsen på fasaden och ingenjörens kommentar med nästa steg hålls ihop.' },
};
interface SizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  intrinsicWidth?: number;
  intrinsicHeight?: number;
}
// Box consumes width/height as CSS properties, so forward intrinsic dimensions separately.
const SizedImage = React.forwardRef<HTMLImageElement, SizedImageProps>(function SizedImage(
  { intrinsicWidth, intrinsicHeight, alt, ...props }, ref,
) {
  return <img {...props} ref={ref} alt={alt} width={intrinsicWidth} height={intrinsicHeight} />;
});

export function PublicImage({ image, alt, eager = false, fullResolution = false, sizes = photoSizes, onError, sx }: {
  image: keyof typeof images; alt: string; eager?: boolean; fullResolution?: boolean;
  sizes?: string; onError: () => void; sx?: SxProps<Theme>;
}) {
  const asset = images[image];
  return <Box component="picture" sx={{ display: 'contents' }}>
    {!fullResolution && asset.srcSet && <source type="image/webp" srcSet={asset.srcSet} sizes={sizes} />}
    <Box component={SizedImage} src={asset.src} alt={alt} intrinsicWidth={asset.width} intrinsicHeight={asset.height}
      loading={eager || fullResolution ? 'eager' : 'lazy'} decoding="async" onError={onError} sx={sx} />
  </Box>;
}
export function Photo({ image, eager = false, locale = 'sv', sizes }: { image: PhotoKey; eager?: boolean; locale?: PublicLocale; sizes?: string }) {
  const [failed, setFailed] = useState(false);
  const asset = images[image];
  return <Box component="figure" sx={{ m: 0 }}>
    {failed ? <ImagePlaceholder label={locale === 'sv' ? 'Bilden kunde inte laddas' : 'The image could not be loaded'} /> : <PublicImage image={image} alt={locale === 'sv' ? asset.alt : {
      facade: 'Brick façade and drone during a KlaraView flight',
      pilot: 'Drone pilot by a property in Malmö',
      controller: 'Pilot controller showing a building during a flight',
    }[image]} eager={eager} sizes={sizes} onError={() => setFailed(true)}
      sx={{ width: '100%', height: { xs: 360, md: 540 }, display: 'block', objectFit: 'cover', objectPosition: image === 'facade' ? '35% 68%' : 'center' }} />}
    <Typography component="figcaption" sx={{ fontSize: 12, mt: 1.5, color: 'inherit', opacity: .8 }}>{locale === 'sv' ? asset.caption : {
      facade: 'KlaraView setting photograph. Illustrative markers are not a damage assessment of the building.',
      pilot: 'From KlaraView work on site.',
      controller: 'Image collection with a person behind the technology.',
    }[image]}</Typography>
  </Box>;
}
const portalViewSizes = '(min-width: 1320px) 1224px, (min-width: 900px) calc(100vw - 96px), (min-width: 600px) calc(100vw - 64px), calc(100vw - 40px)';
const portalViewsEnglish: Record<PortalViewKey, { alt: string; caption: string }> = {
  portalHero: {
    alt: 'Close-up from the KlaraView portal: marked crack in the render below the eaves',
    caption: 'Marked crack in a KlaraView drone image. Cropped example from the portal.',
  },
  portalPortfolio: {
    alt: 'The KlaraView portal listing sample properties, with one property marked as high priority',
    caption: 'Portal view with sample properties, shown in Swedish. One property is illustratively marked as high priority based on an assessment, not on finding count or AI confidence.',
  },
  portalReview: {
    alt: "The KlaraView portal showing a marked crack in a cornice with the engineer's comment and recommendation beside the image",
    caption: "Example portal view, shown in Swedish. The image, its location on the façade and the engineer's comment with the next step stay together.",
  },
};
// Prepared portal imagery keeps its full aspect ratio; the hero crop is generated offline.
export function PortalView({ image, locale = 'sv', dark = false, eager = false, sizes = portalViewSizes }: {
  image: PortalViewKey; locale?: PublicLocale; dark?: boolean; eager?: boolean; sizes?: string;
}) {
  const [failed, setFailed] = useState(false);
  const asset = images[image];
  const copy = locale === 'sv' ? asset : portalViewsEnglish[image];
  return <Box component="figure" sx={{ m: 0 }}>
    {failed ? <ImagePlaceholder label={locale === 'sv' ? 'Bilden kunde inte laddas' : 'The image could not be loaded'} /> : <Box sx={{ border: `1px solid ${dark ? '#475246' : colors.line}`, bgcolor: colors.white, boxShadow: dark ? '0 24px 60px rgba(0,0,0,.35)' : '0 24px 60px rgba(36,41,36,.12)', lineHeight: 0 }}>
      <PublicImage image={image} alt={copy.alt} eager={eager} sizes={sizes} onError={() => setFailed(true)} sx={{ width: '100%', height: 'auto', display: 'block' }} />
    </Box>}
    <Typography component="figcaption" sx={{ fontSize: 12, mt: 1.5, color: 'inherit', opacity: .8 }}>{copy.caption}{' '}
      {!failed && <Box component="a" href={asset.src} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', display: { md: 'none' } }}>{locale === 'sv' ? 'Visa bilden i full storlek' : 'View the full-size image'}</Box>}
    </Typography>
  </Box>;
}
export function ImagePlaceholder({ label }: { label: string }) {
  return <Box role="img" aria-label={label} sx={{ minHeight: 250, border: `1px dashed ${colors.muted}`, bgcolor: colors.soft, color: colors.muted, display: 'grid', placeContent: 'center', textAlign: 'center', p: 4 }}>
    <Box component="svg" viewBox="0 0 100 80" aria-hidden="true" sx={{ width: 90, mx: 'auto', mb: 2, fill: 'none', stroke: 'currentColor', strokeWidth: 1 }}>
      <path d="M10 70V30l25-20v60M35 35l25-15v50M60 45l30-20v45M5 70h90M17 40h10m-10 10h10m-10 10h10M42 42h11m-11 12h11m15-4h14m-14 10h14" />
    </Box><Typography sx={{ fontFamily: fonts.mono, fontSize: 12 }}>{label}</Typography>
  </Box>;
}
export function ContactBand({ accent = false, locale = 'sv', homepage = false }: { accent?: boolean; locale?: PublicLocale; homepage?: boolean }) {
  return <Chapter dark tone={accent ? 'rust' : undefined}><Box sx={splitSx}><Box>
    <Typography sx={{ fontFamily: fonts.mono, fontSize: 12, letterSpacing: '.08em', mb: 2.5, color: accent ? colors.white : colors.copper }}>{locale === 'sv' ? 'NÄSTA STEG' : 'NEXT STEP'}</Typography>
    <Typography variant="h2" sx={headingSx}>{locale === 'sv' ? <>Vad vill du få<br />bättre kontroll över?</> : <>What would you like<br />to understand better?</>}</Typography>
    <Typography sx={{ mt: 3, maxWidth: 450, color: accent ? colors.white : '#CFD5CC' }}>{locale === 'sv' ? 'Berätta vad du står inför. Vi hjälper dig hitta rätt nästa steg.' : 'Tell us what you are facing. We will help you identify the right next step.'}</Typography>
  </Box><Box sx={{ justifySelf: { md: 'end' }, display: 'grid', justifyItems: 'start', gap: 2 }}>
    <Box sx={accent ? { '& .MuiButton-root': { bgcolor: colors.paper, color: colors.rust, '&:hover': { bgcolor: colors.white } } } : {}}>
      <Action href={publicPath('contact', locale)}>{homepage ? homeContactLabel[locale] : locale === 'sv' ? 'Kontakta oss' : 'Contact us'}</Action>
    </Box>
    <Box component="a" href={contactPhone.href} sx={{ color: 'inherit', textUnderlineOffset: 4 }}>{contactPhone.label}</Box>
  </Box></Box></Chapter>;
}
